<template>
  <div :class="['rewards-tracker', `rewards-tracker--${size}`]">
    <div
      class="rewards-tracker__heading"
      v-html="renderMarkdownToHtml(heading)"
    />

    <div class="rewards-tracker__track">
      <div
        class="rewards-tracker__rail"
        aria-hidden="true"
      />
      <div
        class="rewards-tracker__rail-fill"
        :style="railFillStyle"
        aria-hidden="true"
      />

      <ol class="rewards-tracker__nodes">
        <li
          v-for="tier in tiers"
          :key="tier.promotionId"
          :class="[
            'rewards-tracker__tier',
            `rewards-tracker__tier--${renderedState(tier)}`,
            { 'rewards-tracker__tier--celebrating': isCelebratingNode(tier) }
          ]"
        >
          <span class="rewards-tracker__node">
            <FontAwesomeIcon
              v-if="renderedState(tier) === RewardTierState.Unlocked"
              :icon="appIcons.faCheck"
              class="rewards-tracker__node-icon"
            />
            <img
              v-else-if="tier.iconUrl"
              :src="tier.iconUrl"
              class="rewards-tracker__node-img"
              alt=""
            />
            <FontAwesomeIcon
              v-else
              :icon="nodeIcon(tier)"
              class="rewards-tracker__node-icon"
            />
          </span>
          <span class="rewards-tracker__label">{{ nodeLabel(tier) }}</span>
        </li>
      </ol>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import useAppIcons from '@/composables/appIcons'
import { renderMarkdownToHtml } from '@/composables/contentful'
import { rewardRailFillPercent } from '@/lib/rewardLadder'

import { type RewardTier, RewardTierState } from '@/types/RewardTier'

import { PromotionType } from '@VelocitySquared/sunny16-commerce-dataobjects/enums'

import { RewardCelebrationStage } from './RewardCelebrationStage'
import { RewardsTrackerSize } from './RewardsTrackerSize'

/** Default node icon per promotion type. Unlocked tiers always show a check instead. */
const NODE_ICON_BY_TYPE: Record<PromotionType, string[]> = {
  [PromotionType.SubtotalDiscount]: ['fas', 'tag'],
  [PromotionType.ShippingDiscount]: ['far', 'box'],
  [PromotionType.BundleDiscount]: ['fas', 'gift'],
  [PromotionType.AdditionalBundle]: ['fas', 'gift']
}

/**
 * Pure presentational, props-driven rewards ladder. Renders every reward tier as a milestone node
 * along one track, with a heading line above it. Knows nothing about the store or Contentful — the
 * `tiers`, the resolved `heading`, and the `size` are all supplied by an upstream data layer
 * (Iteration 2b). See the Rewards Expansion guide §3.1 and ADR 003.
 */
export default defineComponent({
  name: 'RewardsTracker',
  setup() {
    return {
      ...useAppIcons(),
      renderMarkdownToHtml,
      RewardTierState,
      RewardCelebrationStage
    }
  },
  props: {
    /** All reward tiers, pre-sorted by threshold and state-stamped (from `catalog.rewardTiers`). */
    tiers: {
      type: Array as PropType<RewardTier[]>,
      required: true
    },
    /** Fully resolved heading line (markdown allowed); the data layer templates it upstream. */
    heading: {
      type: String,
      required: true
    },
    size: {
      type: String as PropType<RewardsTrackerSize>,
      default: RewardsTrackerSize.Desktop
    },
    /**
     * Rail fill as a percentage, overriding the value derived from `tiers`.
     *
     * The unlock sequence drives the bar on its own clock — it holds the pre-mutation position until
     * the 700ms beat and then runs to the new one over 800ms — while `tiers` have already updated to
     * the post-mutation state. Without an override the bar would jump the moment the cart response
     * landed and there would be nothing left to animate. `null` returns the bar to `tiers`.
     */
    fillPercent: {
      type: Number as PropType<number | null>,
      default: null
    },
    /**
     * CSS transition applied to the rail fill while the sequence owns it.
     *
     * This is the one place the resting behaviour and the celebration collide: the stylesheet carries
     * `transition: width 0.3s ease` for ordinary subtotal changes, which would otherwise race an
     * 800ms sequence fill. Supplying this as an inline style wins over the stylesheet for the length
     * of the run, and dropping it hands `width` straight back to the resting transition.
     */
    fillTransition: {
      type: String as PropType<string | null>,
      default: null
    },
    /** The tier currently celebrating. Only one does per sequence — see {@link RewardCelebrationStage}. */
    celebratingTierId: {
      type: String as PropType<string | null>,
      default: null
    },
    /** How far that tier's node has got through the celebration. Inert at `None`. */
    celebrationStage: {
      type: String as PropType<RewardCelebrationStage>,
      default: RewardCelebrationStage.None
    },
    /**
     * Tiers whose nodes should still render pre-crossing, even though their data says unlocked.
     *
     * On a single-tier crossing this is just the celebrating tier. On a multi-tier one it is **every**
     * tier the mutation unlocked — otherwise the ones that are not being celebrated sit checked from
     * the first frame, while the bar has not yet reached them. They resolve together at the checkmark
     * beat; only the celebrating tier additionally takes the magenta and the pop, which is what
     * "celebrate the last reward unlocked" amounts to on screen.
     */
    heldTierIds: {
      type: Array as PropType<readonly string[]>,
      default: () => []
    }
  },
  computed: {
    railFillStyle(): Record<string, string> {
      const style: Record<string, string> = {
        width: this.fillPercent === null ? this.railFillPercent : `${this.fillPercent}%`
      }
      if (this.fillTransition) style.transition = this.fillTransition

      return style
    },
    /**
     * The rail's resting width, from the tier data alone. The rule lives in `lib/rewardLadder.ts`
     * because the unlock sequence needs the same numbers at two *other* subtotals to build its
     * `fromPct` / `toPct` — see {@link rewardRailFillPercent}.
     */
    railFillPercent(): string {
      return `${rewardRailFillPercent(this.tiers)}%`
    }
  },
  methods: {
    /** Promotion-type default node icon (used when no authored icon and not unlocked). */
    nodeIcon(tier: RewardTier): string[] {
      // The record covers every known `PromotionType`; the fallback guards a backend-driven type the
      // frontend enum doesn't map yet (the index is total at compile time but not at runtime).
      return NODE_ICON_BY_TYPE[tier.type] ?? this.appIcons.faGift
    },
    /** True for the one tier the running sequence is celebrating. */
    isCelebratingNode(tier: RewardTier): boolean {
      return (
        this.celebrationStage !== RewardCelebrationStage.None &&
        tier.promotionId === this.celebratingTierId
      )
    },
    /**
     * The state a node should *render* as, which is not always the state its data is in.
     *
     * During a sequence a crossed tier's data already reads `Unlocked` — the cart response has landed
     * — but the beats have not caught up. Until the checkmark beat it must not show a checkmark, so it
     * renders as `Current`: the state it was in before the crossing. Tiers this mutation did not touch
     * render from their own state, always.
     */
    renderedState(tier: RewardTier): RewardTierState {
      return this.heldTierIds.includes(tier.promotionId) ? RewardTierState.Current : tier.state
    },
    nodeLabel(tier: RewardTier): string {
      if (this.renderedState(tier) === RewardTierState.Unlocked) {
        return tier.unlockedLabel ?? tier.label
      }
      return tier.label
    }
  }
})
</script>

<style scoped lang="scss">
// Figma tokens (SY26-27 Global Ordering UI, node 10482-39183):
//   magenta-600 #bb4a92 · gray-300 #bbbbbb · gray-100 #eeeeee · gray-600 #555555 · white #ffffff
//   pink drop-shadow #BB4A9266 radius 12 · node ⌀ 40 · body-xs 12/16 (bold when unlocked)
// Mobile-first: base styles are the compact (Mobile) form; Desktop layers up.

$rt-magenta: #bb4a92;
$rt-gray-node: #bbbbbb;
$rt-rail: #eeeeee;
$rt-gray-label: #555555;

// Sequence durations, mirroring `lib/rewardSequenceTimings.ts`. SCSS cannot import from TS, so these
// are the one unavoidable duplication — kept adjacent and named identically so a spec change is a
// two-line edit rather than a hunt. The JS side owns *when* a beat fires; these own how long the CSS
// that beat triggers takes to run.
$rt-node-magenta-ms: 400; // NODE_MAGENTA_TRANSITION_MS
$rt-check-pop-ms: 350; // CHECK_POP_MS

.rewards-tracker {
  font-family: 'Roboto', sans-serif;
  color: $rt-gray-label;

  &__heading {
    text-align: center;
    font-size: 0.875rem;
    line-height: 1.3125rem;
    margin-bottom: 0.75rem;
    color: #000;

    &:deep(p) {
      margin: 0;
    }

    &:deep(strong) {
      font-weight: 700;
    }
  }

  &__track {
    position: relative;
  }

  // (node radius = 20px), inset by half a column so they start/end at the first/last node center.
  &__rail,
  &__rail-fill {
    position: absolute;
    top: 20px;
    height: 10px;
    transform: translateY(-50%);
    border-radius: 999px;
  }

  &__rail {
    left: 0;
    right: 0;
    background-color: $rt-rail;
  }

  &__rail-fill {
    left: 0;
    background-color: $rt-magenta;
    box-shadow: 0 0 12px 0 #bb4a9266;
    transition: width 0.3s ease;
  }

  &__nodes {
    position: relative;
    display: flex;
    align-items: flex-start;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__tier {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    min-width: 0;
  }

  &__node {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: $rt-gray-node;
    color: #fff;
    flex: 0 0 auto;
    // The 1,200ms beat's 400ms CSS transition. Harmless at rest — a node's colour only changes when
    // its tier's state does, which outside a sequence happens on a cart response anyway.
    transition:
      background-color #{$rt-node-magenta-ms}ms ease,
      box-shadow #{$rt-node-magenta-ms}ms ease;
  }

  // Visually match the 18px Contentful node image. FA glyphs carry internal viewBox padding (the
  // regular-weight box especially), so they render smaller than an equal-pixel image that fills its
  // box — the font-size is set above 18px so the drawn glyph fills ~18px to match.
  &__node-icon {
    font-size: 1.375rem;
  }

  &__node-img {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }

  &__label {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    line-height: 1rem;
    color: $rt-gray-label;
    padding: 0 0.25rem;
  }

  // Unlocked: magenta node + white check + 1px white border + pink glow + bold magenta label.
  &__tier--unlocked {
    .rewards-tracker__node {
      background-color: $rt-magenta;
      border: 1px solid #fff;
      box-shadow: 0 0 12px 0 #bb4a9266;
    }

    .rewards-tracker__label {
      color: $rt-magenta;
      font-weight: 700;
    }
  }

  // The 1,200ms beat. `renderedState` still reports this tier as `Current`, so the rules above have
  // not applied yet — the node takes the magenta and the glow while keeping its reward icon and its
  // grey label. Those two land together at 1,800ms, when `renderedState` flips to `Unlocked` and the
  // block above takes over. This selector is only ever active during a sequence.
  &__tier--celebrating {
    .rewards-tracker__node {
      background-color: $rt-magenta;
      border: 1px solid #fff;
      box-shadow: 0 0 12px 0 #bb4a9266;
    }
  }

  // The 1,800ms scale pop, `1 → 1.25 → 1`. Runs on the node itself rather than the glyph so the glow
  // scales with it — the spec calls for the glow to intensify on the same beat, and a shadow that
  // grows with the element reads as exactly that without a second animated property.
  &__tier--celebrating.rewards-tracker__tier--unlocked {
    .rewards-tracker__node {
      animation: rewards-tracker-check-pop #{$rt-check-pop-ms}ms ease-in-out;
    }
  }

  // Desktop: centered heading, larger heading text, more horizontal breathing room.

  // Desktop: centered heading, larger heading text, more horizontal breathing room.
  &--desktop {
    .rewards-tracker__heading {
      text-align: center;
      font-size: 1rem;
      line-height: 1.5rem;
      margin-bottom: 1.25rem;
    }

    .rewards-tracker__label {
      font-size: 0.75rem;
    }
  }
}

@keyframes rewards-tracker-check-pop {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.25);
  }

  100% {
    transform: scale(1);
  }
}

// Nothing in the celebration is essential to understanding the cart, so all of it goes. The node
// still ends magenta with its checkmark — the sequence's end state is reached, just not performed.
// A5 routes the machine through the same `settle()` path; this covers the CSS half.
@media (prefers-reduced-motion: reduce) {
  .rewards-tracker__node {
    transition: none;
  }

  .rewards-tracker__tier--celebrating.rewards-tracker__tier--unlocked .rewards-tracker__node {
    animation: none;
  }
}
</style>
